import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Code, 
  Workflow, 
  Share2, 
  BarChart3, 
  Bot, 
  Video,
  MessageSquare,
  ArrowRight 
} from "lucide-react";
import analyticsImage from "@/assets/ai-analytics.jpg";
import businessImage from "@/assets/business-ai.jpg";

const Services = () => {
  const services = [
    {
      icon: Code,
      title: "Application Development",
      description: "We create custom applications tailored to your specific business needs. From concept to deployment, our full-stack development services deliver scalable, secure, and user-friendly applications that streamline processes, enhance customer engagement, and drive business growth with modern technology stacks.",
      features: [
        "Full-stack web and mobile development",
        "Custom business logic and workflows",
        "API integration and third-party services",
        "Cloud deployment and scalability",
        "Real-time data synchronization",
        "Ongoing maintenance and support"
      ]
    },
    {
      icon: Workflow,
      title: "AI Agent Workflows & Process Automation",
      description: "Transform your business operations with intelligent workflow automation designed specifically for your processes. Our AI agents learn your business logic, automate complex multi-step workflows, and integrate seamlessly with your existing systems. Eliminate manual data entry, reduce errors, and accelerate decision-making while your team focuses on strategic initiatives that drive growth.",
      features: [
        "Multi-step workflow automation",
        "Intelligent document processing",
        "Cross-platform system integration",
        "Real-time process monitoring and analytics",
        "Custom business rule implementation",
        "Automatic error detection and resolution",
        "Scalable to handle enterprise volumes",
        "Continuous learning and optimization"
      ]
    },
    {
      icon: Share2,
      title: "Social Media Content with AI-Driven Analytics",
      description: "Stop wasting time on ineffective content. Our AI-driven analytics ensure your social media strategy is targeted, engaging, and delivers measurable results.",
      features: [
        "Data-driven content strategy",
        "Audience insights",
        "Performance tracking",
        "ROI optimization"
      ]
    },
    {
      icon: BarChart3,
      title: "AI Brand & Marketing Analyzation Tools",
      description: "Gain a competitive edge with our AI-powered analytics. Stay ahead by understanding market trends, customer behavior, and competitor strategies.",
      features: [
        "Market trend analysis",
        "Customer behavior insights",
        "Competitor intelligence",
        "Strategic recommendations"
      ]
    },
    {
      icon: Bot,
      title: "Custom AI Copilot",
      description: "Empower your team with a personalized AI copilot built just for your brand. This tool will assist with content creation, data analysis, and decision-making, boosting overall productivity.",
      features: [
        "Brand-specific training",
        "Content generation",
        "Data analysis support",
        "Enhanced productivity"
      ]
    },
    {
      icon: Video,
      title: "Video & Podcast Production",
      description: "Elevate your brand with high-quality video and podcast content. We handle all aspects of production, from concept to distribution, ensuring your message reaches your target audience effectively.",
      features: [
        "Full production services",
        "Professional quality",
        "Distribution strategy",
        "Brand storytelling"
      ]
    },
    {
      icon: MessageSquare,
      title: "AI and Marketing Consultant",
      description: "Modernize your business and stay ahead of the competition. We offer expert consultation services to help you integrate AI into your marketing strategy and achieve your business goals.",
      features: [
        "Strategic planning",
        "AI integration guidance",
        "Implementation support",
        "Ongoing optimization"
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 md:px-6">
        {/* Hero Section */}
        <section className="mb-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
            Our Services
          </h1>
          <div className="h-1 w-24 bg-accent mx-auto mb-8"></div>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto animate-fade-in">
            Voice Vision Media helps businesses modernize their marketing and media strategies with cutting-edge AI solutions. We offer a comprehensive suite of services designed to increase brand visibility, improve efficiency, and maximize ROI.
          </p>
        </section>

        {/* Services Grid */}
        <section className="mb-16">
          <div className="grid gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-2 hover:border-accent transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent mr-2"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Integration Section with Images */}
        <section className="mb-16 grid md:grid-cols-2 gap-8">
          <div className="bg-secondary/30 rounded-2xl p-8 md:p-12 flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Seamless Integration & Workflow Benefits</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Integration Capabilities</h3>
                <p className="text-lg text-muted-foreground">
                  Our AI Virtual Employees integrate seamlessly with 50+ business platforms including CRMs (Salesforce, HubSpot), scheduling tools (Calendly, Google Calendar), communication platforms (Slack, Microsoft Teams), project management systems (Asana, Monday.com), and custom databases. APIs and webhooks ensure real-time data synchronization across your entire technology ecosystem.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Key Benefits</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                    <span><strong>60-80% time savings</strong> on repetitive administrative tasks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                    <span><strong>95% reduction in human errors</strong> through automated data validation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                    <span><strong>24/7 operational efficiency</strong> without additional staffing costs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0"></div>
                    <span><strong>Rapid deployment</strong> in 48-72 hours with comprehensive training</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="grid grid-rows-2 gap-4">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={analyticsImage}
                alt="AI Analytics Dashboard"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={businessImage}
                alt="Business AI Solutions"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-primary text-primary-foreground rounded-2xl p-8 md:p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Business with AI?
          </h2>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Contact us today to schedule a consultation and discover how Voice Vision Media can help you achieve your business objectives.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold">
            <Link to="/contact">
              Schedule a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </section>
      </div>
    </div>
  );
};

export default Services;
